/**
 * PhotoLightbox — iter668i
 *
 * A single, share-across-the-app "FLYT liquid glass" photo viewer.
 * Renders inside a React portal on <body> so it's guaranteed to sit
 * above every fixed BottomNav / sticky header / modal overlay, and
 * so scroll-lock doesn't trip on the caller's own transform: contexts.
 *
 * Wraps the photo in a rounded, blurred, glassmorphic frame (matches
 * the rest of the FLYT surface language — see the PulsePill / Push-
 * to-Network pill / FoundingPhotoBanner). Backdrop is a heavy blur
 * of whatever is behind it plus a `bg-black/70` scrim, so the photo
 * pops without stealing all the ambient context.
 *
 * Interactions:
 *   • Tap anywhere OUTSIDE the photo (backdrop, top-safe-area, the
 *     dead space around the framed image) → close.
 *   • Explicit "×" close button in the top-right of the frame.
 *   • Escape key → close (keyboard / iPad accessory).
 *   • Body scroll is locked while open so scrolling the underlying
 *     grid doesn't shift the viewport under the photo.
 *
 * Why is this its OWN component (instead of an inline <Dialog>)?
 * Two calling surfaces need it — the Media Hub grid AND the public
 * network profile — and both had different pre-existing overlays
 * (Cloudinary raw-link on the network side, a Safari long-press
 * sheet on the Hub side). Centralising the viewer means the frame,
 * the tap-to-close hitbox, and the "no Cloudinary URL leaking"
 * guarantee are all one code path.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Download, Loader2 } from 'lucide-react';
import { resolveImageUrl } from './ImageUpload';
import { downloadAssets } from '../lib/mediaDownload';
// iter689f · Serve a SCREEN-SIZED, auto-format/quality copy in the
// lightbox instead of the raw full-res original (contributor uploads can
// be ~9.5 MB → ~6 s to paint on native iOS). Cloudinary resizes + picks
// WebP/AVIF on the edge, so a phone loads a ~200-400 KB image in well
// under a second. Non-Cloudinary URLs pass through untouched.
const screenOptimized = (raw) => {
  const u = resolveImageUrl(raw);
  if (!u || typeof u !== 'string' || !u.includes('/image/upload/')) return u;
  if (/\/image\/upload\/(f_auto|q_auto|w_\d)/.test(u)) return u; // already transformed
  return u.replace('/image/upload/', '/image/upload/f_auto,q_auto:good,w_1600,c_limit,dpr_2.0/');
};
// iter800 · Jay: opening a photo should feel instant, not wait on the
// full 1600px derivative. Same trick the Media Hub grid tiles already
// use for their own placeholder — a ~1-3 KB, aspect-preserving (c_limit,
// not c_fill — a crop here would visibly mismatch the sharp image
// underneath) sliver that Cloudinary can generate and the phone can
// download well under 100ms even on a slow connection. It paints first,
// blurred, then the real screenOptimized() image crossfades on top once
// it's actually decoded. Returns null for non-Cloudinary URLs so the
// caller can fall straight back to the single-image path.
const tinyPlaceholder = (raw) => {
  const u = resolveImageUrl(raw);
  if (!u || typeof u !== 'string' || !u.includes('/image/upload/')) return null;
  return u.replace('/image/upload/', '/image/upload/w_32,c_limit,q_auto:low,f_auto/');
};
// iter768 · Video assets. `resourceType === 'video'` swaps the frame
// to a native <video> player (fast, compressed in-app preview) with a
// "Download full quality" affordance next to it, matching what Jay
// asked for: quick in-app viewing, full quality on download.
const PhotoLightbox = ({
  open, url, alt = '', onClose,
  resourceType, previewUrl, posterUrl, downloadUrl,
}) => {
  const isVideo = resourceType === 'video';
  // iter790 · Jay: "Full quality" was a plain <a href> — on iOS that just
  // opened the raw Cloudinary URL in a browser instead of saving
  // anything. Now goes through the same native save-to-Photos path the
  // Media Hub's own "Save to camera roll" button already uses.
  const [savingVideo, setSavingVideo] = useState(false);
  // iter796 · Jay: opening a video used to visibly shrink small then pop
  // back to full size — the <video> box has no fixed dimensions, so
  // before the poster/metadata resolves the browser renders it at its
  // default intrinsic size (~300x150) and reflows once real dimensions
  // are known. Giving the video frame a fixed portrait aspect ratio
  // (matching VideoDecisionLightbox's viewer) removes that dependency on
  // natural size entirely, and a loading spinner covers the frame until
  // it's actually ready to play instead of the size jump being the only
  // "something's happening" signal.
  const [videoLoading, setVideoLoading] = useState(true);
  useEffect(() => { setVideoLoading(true); }, [url]);
  // iter800 · Tracks whether the full-quality photo has decoded yet, so
  // the tiny blurred placeholder can crossfade out instead of the frame
  // just sitting blank while it loads.
  const [sharpLoaded, setSharpLoaded] = useState(false);
  useEffect(() => { setSharpLoaded(false); }, [url]);
  // iter801 · Jay: the blur-up placeholder shrank the WHOLE lightbox down
  // to a postage stamp — the frame's size was being driven by the tiny
  // 32px-wide placeholder's own intrinsic pixel dimensions (`w-auto
  // h-auto` never enlarges a small image past its native size), not the
  // full photo's real display size. Fix: read the placeholder's aspect
  // ratio the instant it loads (same ratio as the sharp image — both are
  // `c_limit` derivatives of the same source, no crop) and size the
  // frame from that ratio directly, capped at the same 92vw/86vh the
  // single-image path already used — so the frame is full-size
  // immediately, just blurry, then sharpens in place with zero jump.
  const [aspect, setAspect] = useState(null);
  useEffect(() => { setAspect(null); }, [url]);
  const saveVideoFullQuality = async (e) => {
    e.stopPropagation();
    if (!downloadUrl || savingVideo) return;
    setSavingVideo(true);
    try {
      const saved = await downloadAssets([{ url: downloadUrl, isVideo: true }]);
      if (saved > 0) toast.success('Saved to Photos.');
    } catch {
      toast.error('Could not save the video. Try again.');
    } finally {
      setSavingVideo(false);
    }
  };
  // Body scroll-lock while open. Restores whatever the caller had
  // (some pages set `overflow: hidden` themselves).
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
  // ESC to close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open || !url) return null;
  // Portal to body so we escape any transform: parent that would
  // otherwise trap `position: fixed` (a common gotcha inside
  // Framer Motion / animated cards).
  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center animate-flyt-lightbox-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="photo-lightbox"
    >
      {/* Backdrop — heavy blur of what's behind + dark scrim. Tap
          fires the onClick handler on the outer div (bubbles). */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-2xl" />
      {/* Photo frame. `onClick` stops propagation so tapping the
          image itself doesn't dismiss — only the surrounding
          backdrop / dead-space does. The frame gets the FLYT
          glass treatment: rounded-3xl, subtle white ring, thin
          inner shadow highlight, blurred bg for the letterbox. */}
      <div
        className={`relative rounded-3xl overflow-hidden shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)] ring-1 ring-white/15 bg-white/[0.04] backdrop-blur-xl ${isVideo ? 'w-full max-w-[380px] aspect-[9/16] max-h-[80vh]' : 'max-w-[92vw] max-h-[86vh]'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {isVideo ? (
          <>
            <video
              src={previewUrl || url}
              poster={posterUrl ? screenOptimized(posterUrl) : undefined}
              controls
              autoPlay
              playsInline
              // iter787 · Jay: opening a video in the Media Hub felt slow —
              // the poster shows instantly, but without this the browser
              // waited to start fetching until the play attempt, adding a
              // second round-trip's worth of delay on top of Cloudinary's
              // on-demand transcode. "auto" tells it to start buffering the
              // moment the lightbox mounts.
              preload="auto"
              className="w-full h-full object-contain select-none bg-black"
              onContextMenu={(e) => e.preventDefault()}
              onLoadedData={() => setVideoLoading(false)}
              onCanPlay={() => setVideoLoading(false)}
              onPlaying={() => setVideoLoading(false)}
              onWaiting={() => setVideoLoading(true)}
            />
            {videoLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 pointer-events-none">
                <Loader2 className="w-9 h-9 text-white/90 animate-spin" />
              </div>
            )}
          </>
        ) : (() => {
          const tiny = tinyPlaceholder(url);
          if (!tiny) {
            // Non-Cloudinary URL — no low-res derivative to lean on,
            // fall straight back to the single sharp image.
            return (
              <img
                src={screenOptimized(url)}
                alt={alt}
                draggable={false}
                decoding="async"
                fetchpriority="high"
                className="block max-w-[92vw] max-h-[86vh] w-auto h-auto object-contain select-none"
                onContextMenu={(e) => e.preventDefault()}
              />
            );
          }
          return (
            <div
              className="relative"
              // Largest box matching the photo's real aspect ratio that
              // still fits within 92vw × 86vh — same cap the plain
              // single-image path below uses, just computed up front
              // instead of left to the image's own intrinsic size (which
              // is what let a 32px-wide placeholder collapse the whole
              // frame down to nothing).
              style={aspect ? {
                width: `min(92vw, calc(86vh * ${aspect}))`,
                height: `min(86vh, calc(92vw / ${aspect}))`,
              } : { width: '60vw', height: '60vh' }}
            >
              {/* Instant blurred placeholder — stretched to fill the
                  frame (not shown at its own tiny native resolution)
                  and blurred hard enough that the low pixel count never
                  reads as blocky. */}
              <img
                src={tiny}
                alt=""
                aria-hidden="true"
                draggable={false}
                className="absolute inset-0 w-full h-full object-contain select-none"
                style={{ filter: 'blur(16px)', transform: 'scale(1.06)' }}
                onLoad={(e) => {
                  const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                  if (w && h) setAspect(w / h);
                }}
                onContextMenu={(e) => e.preventDefault()}
              />
              <img
                src={screenOptimized(url)}
                alt={alt}
                draggable={false}
                decoding="async"
                fetchpriority="high"
                className="absolute inset-0 w-full h-full object-contain select-none transition-opacity duration-300 ease-out"
                style={{ opacity: sharpLoaded ? 1 : 0 }}
                onLoad={(e) => {
                  setSharpLoaded(true);
                  const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                  if (w && h) setAspect(w / h);
                }}
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
          );
        })()}
        {/* Subtle top-highlight for the liquid-glass look. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white/10 to-transparent" />
        {isVideo && downloadUrl && (
          <button
            type="button"
            onClick={saveVideoFullQuality}
            disabled={savingVideo}
            className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md ring-1 ring-white/20 text-white text-[10px] uppercase tracking-widest font-black hover:bg-black/80 disabled:opacity-60 transition"
            data-testid="photo-lightbox-download-full"
          >
            {savingVideo ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" strokeWidth={2.5} />} Full quality
          </button>
        )}
      </div>
      {/* Close button — floating, ring-styled, top-right of the
          viewport (not the frame) so it's easy to tap even when
          the frame is tall. Also stops propagation so the tap
          doesn't double-fire the backdrop close. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute top-[calc(env(safe-area-inset-top,0px)+16px)] right-4 w-10 h-10 rounded-full bg-black/60 backdrop-blur-md ring-1 ring-white/20 text-white hover:bg-black/80 flex items-center justify-center transition"
        aria-label="Close photo"
        data-testid="photo-lightbox-close"
      >
        <X className="w-5 h-5" strokeWidth={2.5} />
      </button>
      {/* Local keyframe — kept inline so the component is self-
          contained and can be dropped into any page without a
          tailwind.config change. */}
      <style>{`
        @keyframes flyt-lightbox-in {
          from { opacity: 0; transform: scale(0.98); }
          to   { opacity: 1; transform: scale(1); }
        }
        .animate-flyt-lightbox-in {
          animation: flyt-lightbox-in 180ms cubic-bezier(0.22, 1, 0.36, 1);
        }
      `}</style>
    </div>,
    document.body,
  );
};
export default PhotoLightbox;
